import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChildSummary {
  id: string;
  name: string;
  avatarEmoji: string;
  completedToday: number;
  totalToday: number;
}

interface DashboardData {
  familyName: string;
  petName: string;
  petHappiness: number;
  petMood: string;
  streakDays: number;
  children: ChildSummary[];
}

// ─── Placeholder data — replace with real API calls ──────────────────────────

const MOCK_DATA: DashboardData = {
  familyName: "Familie Müller",
  petName: "Miezi",
  petHappiness: 72,
  petMood: "fröhlich",
  streakDays: 4,
  children: [
    { id: "c1", name: "Lena", avatarEmoji: "🦊", completedToday: 2, totalToday: 3 },
    { id: "c2", name: "Max", avatarEmoji: "🐻", completedToday: 3, totalToday: 3 },
  ],
};

// ─── Quick-action definitions ─────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { emoji: "👶", label: "Kind hinzufügen", route: "/parent/add-child" },
  { emoji: "📋", label: "Routine erstellen", route: "/parent/add-routine" },
  { emoji: "🔔", label: "Erinnerungen", route: "/parent/reminders" },
  { emoji: "⚙️", label: "Einstellungen", route: "/parent/settings" },
] as const;

// ─── Component ────────────────────────────────────────────────────────────────

export function ParentDashboard(): React.ReactElement {
  const [data] = useState<DashboardData>(MOCK_DATA);
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-4 p-4 pb-8">
      {/* Header row */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-lg font-bold text-gray-900">{data.familyName}</h1>
          <p className="text-sm text-gray-500">Eltern-Dashboard</p>
        </div>
        <div className="flex flex-col items-center rounded-2xl bg-purple-50 px-3 py-2">
          <span className="text-xs text-purple-500">Streak</span>
          <span className="text-lg font-bold text-purple-700">🔥 {data.streakDays}</span>
        </div>
      </motion.div>

      {/* Pet happiness card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
        className="rounded-2xl bg-white p-4 shadow-sm"
      >
        <div className="mb-2 flex items-center gap-2">
          <span className="text-2xl">🐱</span>
          <div className="flex-1">
            <div className="flex justify-between text-sm">
              <span className="font-semibold text-gray-700">
                {data.petName} · <span className="font-normal text-gray-400">{data.petMood}</span>
              </span>
              <span className="text-gray-500">{data.petHappiness}%</span>
            </div>
          </div>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-gray-100">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${data.petHappiness}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className={`h-full rounded-full ${
              data.petHappiness >= 60
                ? "bg-purple-500"
                : data.petHappiness >= 30
                ? "bg-amber-400"
                : "bg-red-400"
            }`}
          />
        </div>
      </motion.div>

      {/* Children overview */}
      <div>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-400">
          Kinder
        </h2>
        <div className="flex flex-col gap-3">
          {data.children.map((child, index) => {
            const pct =
              child.totalToday > 0
                ? Math.round((child.completedToday / child.totalToday) * 100)
                : 0;

            return (
              <motion.div
                key={child.id}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 + index * 0.07 }}
                className="rounded-2xl bg-white p-4 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-3">
                  <span className="text-3xl">{child.avatarEmoji}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{child.name}</p>
                    <p className="text-xs text-gray-400">
                      {child.completedToday}/{child.totalToday} Aufgaben heute
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      pct === 100
                        ? "bg-green-100 text-green-700"
                        : pct >= 50
                        ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {pct}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 + index * 0.07 }}
                    className={`h-full rounded-full ${
                      pct === 100 ? "bg-green-500" : pct >= 50 ? "bg-amber-400" : "bg-red-400"
                    }`}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Quick actions */}
      <div>
        <h2 className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-400">
          Aktionen
        </h2>
        <div className="grid grid-cols-2 gap-3">
          {QUICK_ACTIONS.map(({ emoji, label, route }, index) => (
            <motion.button
              key={label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + index * 0.06 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => { void navigate(route); }}
              className="flex flex-col items-center gap-2 rounded-2xl bg-white p-4 shadow-sm active:bg-gray-50"
            >
              <span className="text-2xl">{emoji}</span>
              <span className="text-xs font-medium text-gray-700">{label}</span>
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
}
